import { useEffect, useRef, useState } from "react";

type LaenderAbruf = () => Promise<{ laender: string[] }>;

/**
 * Puffer je Abrufweg (Review K4 zum Geschäftspartner-Dialog): Die Liste ändert sich in einer Sitzung nicht, und Dialoge
 * wie die Stammdaten-Bearbeitung hängen bei jedem Öffnen neu ein. Geladen wird deshalb einmal je Client-Methode — die
 * Methode ist der Schlüssel, eine neue Attrappe im Test bekommt so ihren eigenen Abruf. Ein Fehlschlag wird nicht
 * gepuffert: Der nächste Einbau fragt wieder.
 */
const ABRUFE = new WeakMap<LaenderAbruf, Promise<string[]>>();
const GELADEN = new WeakMap<LaenderAbruf, string[]>();

function laden(abruf: LaenderAbruf): Promise<string[]> {
  let laeuft = ABRUFE.get(abruf);
  if (laeuft === undefined) {
    laeuft = Promise.resolve().then(() => abruf()).then((antwort) => {
      GELADEN.set(abruf, antwort.laender);
      return antwort.laender;
    });
    laeuft.catch(() => { ABRUFE.delete(abruf); });
    ABRUFE.set(abruf, laeuft);
  }
  return laeuft;
}

/**
 * Die zulässigen Länderkürzel aus `GET /api/laender` (Spec „Adresszeile", R17) — web darf
 * domain nicht importieren, und eine abgetippte Liste veraltete mit dem nächsten Land still.
 * Scheitert das Laden oder fehlt die Methode (Test-Attrappe), bleibt die Liste leer; die
 * Ortszeile zeigt dann nur den gespeicherten Wert — gespeichert werden kann weiterhin.
 *
 * Geladen wird einmal je Client-Methode (Puffer oben); eine schon geladene Liste steht beim Einbau sofort da. Die
 * Methode wird beim Einbau gelesen, nicht je Aufruf-Identität: eine Pfeilfunktion im Aufruf wäre sonst bei jeder
 * Zeichnung neu und hielte die Zeichnung am Laufen.
 */
export function useLaender(listLaender: LaenderAbruf | undefined): string[] {
  const [laender, setLaender] = useState<string[]>(() =>
    (listLaender === undefined ? undefined : GELADEN.get(listLaender)) ?? []);
  const lade = useRef(listLaender);
  lade.current = listLaender;
  const vorhanden = listLaender !== undefined;
  useEffect(() => {
    const abruf = lade.current;
    if (!vorhanden || abruf === undefined) return;
    let abgebrochen = false;
    laden(abruf)
      .then((liste) => { if (!abgebrochen) setLaender(liste); })
      .catch(() => { /* Rückfall: nur der gespeicherte Wert */ });
    return () => { abgebrochen = true; };
  }, [vorhanden]);
  return laender;
}
